/**
 * customerComponents — Kunden- & Fahrzeug-Teilkomponenten der Rechnungs-/CRM-
 * Module (aus InvoicesPage.jsx ausgelagert, Refactoring Jun-2026).
 *
 * Enthält: CustomerGarage, CustomerActivities, VehicleModal, VehiclesTab,
 * MoveVehicleModal, BranchBreakdown, CustomerSegmentBar, SegmentCampaignModal,
 * CustomerList, EditCustomerModal, CreateCustomerModal.
 *
 * Öffentliche Exporte (auch von InvoicesPage re-exportiert): VehiclesTab,
 * BranchBreakdown, CustomerSegmentBar, CustomerList, CreateCustomerModal.
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserPlus, Download, CheckCircle2, AlertTriangle, Receipt, X, Bell, Mail, ChevronDown, ChevronRight, Car, ArrowRightLeft, Trash2, Filter, Loader, Pencil, Wrench, Bike, MessageCircle, History, User, Archive } from "lucide-react";

import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import BrandCombobox from "../../components/listing/BrandCombobox";
import { MOTO_BRANDS, BIKE_BRANDS, MOTORCYCLE_VEHICLE_TYPES, BIKE_TYPES, BRAND_MODELS } from "../../lib/listingConstants";
import { previewPdfResponse, customerRecipient } from "../../lib/pdfPreview";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { fmtDate, waLink, PaginationBar, ModalShell } from "./invoicesShared";
import CustomerWhatsAppModal from "./CustomerWhatsAppModal";
import { VinWarning } from "../../components/common/VinWarning";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Jul-2026 (User-Wunsch) — Löschfunktionen an Kundenkarten nur für Filialleiter
// (bzw. Inhaber/Admin) sichtbar; Backend erzwingt dieselbe Regel.
const useCanDeleteCustomers = () => {
    const { user } = useAuth();
    return user?.role === "b2b" || user?.role === "admin"
        || (user?.employee_function || "").toLowerCase() === "filialleiter";
};
const DOC_KIND_LABELS = {
    zb1: "ZB1", zb2: "ZB2/Brief", coc: "COC", service_heft: "Service-Heft",
    id: "Ausweis/FS", invoice: "Rechnung/Auftrag", damage: "Schadensfoto",
    werkstattauftrag: "Werkstattauftrag", handschriftlich: "Handschriftliche Notiz",
};

// Jun-2026 (User-Wunsch) — Bereich „Rechnungen/Gutschriften" an der Kundenkarte:
// ALLE Rechnungen, Gutschriften und Stornos des Kunden (egal aus welchem Bereich).
function CustomerInvoices({ customer }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && rows === null) {
            try {
                const { data } = await api.get(`/invoices?customer_id=${customer.id}`);
                setRows(Array.isArray(data) ? data : (data?.items || []));
            } catch { setRows([]); }
        }
    };
    const badge = (inv) => {
        if (inv.is_storno) return ["Storno", "bg-red-100 text-red-800"];
        if (inv.is_credit || inv.is_credit_note) return ["Gutschrift", "bg-emerald-100 text-emerald-800"];
        if (inv.status === "cancelled") return ["Storniert", "bg-red-50 text-red-700"];
        if (inv.status === "draft") return ["Entwurf", "bg-gray-100 text-gray-600"];
        if (inv.paid_at || inv.status === "paid") return ["Bezahlt", "bg-emerald-50 text-emerald-700"];
        return ["Offen", "bg-amber-100 text-amber-800"];
    };
    const openPdf = async (inv) => {
        setBusyId(inv.id);
        try {
            const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
            previewPdfResponse(r, `${inv.is_credit || inv.is_credit_note ? "Gutschrift" : inv.is_storno ? "Storno" : "Rechnung"}_${inv.invoice_number || "ENTWURF"}.pdf`, { recipient: customerRecipient(customer) });
        } catch (e) { toast.error(e.response?.data?.detail || "PDF konnte nicht geladen werden"); }
        finally { setBusyId(null); }
    };
    return (
        <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`cust-invoices-${customer.id}`}>
            <button type="button" onClick={toggle} className="w-full flex items-center justify-between text-xs font-bold text-gray-600 hover:text-[#111827]" data-testid={`cust-invoices-toggle-${customer.id}`}>
                <span className="inline-flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Rechnungen/Gutschriften{rows !== null ? ` (${rows.length})` : ""}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="mt-2 space-y-1">
                    {rows === null && <p className="text-[11px] text-gray-400">Lade …</p>}
                    {rows !== null && rows.length === 0 && <p className="text-[11px] text-gray-400" data-testid={`cust-invoices-empty-${customer.id}`}>Keine Rechnungen oder Gutschriften vorhanden.</p>}
                    {(rows || []).map((inv) => {
                        const [label, cls] = badge(inv);
                        const gross = Number((inv.totals || {}).total_gross || 0);
                        return (
                            <button key={inv.id} type="button" onClick={() => openPdf(inv)} disabled={busyId === inv.id}
                                className="w-full flex items-center justify-between gap-2 text-left text-[11px] rounded-sm border border-gray-100 hover:border-[#111827] px-2 py-1.5 transition-colors"
                                data-testid={`cust-invoice-row-${inv.id}`}>
                                <span className="min-w-0 truncate font-bold text-[#111827]">
                                    {inv.invoice_number || "ENTWURF"}
                                    <span className="ml-1 font-normal text-gray-500">{(inv.invoice_date || inv.created_at || "").slice(0, 10).split("-").reverse().join(".")}</span>
                                </span>
                                <span className="shrink-0 inline-flex items-center gap-1.5">
                                    <span className="font-bold">{gross.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</span>
                                    <span className={`rounded-full px-1.5 py-0.5 font-bold ${cls}`}>{label}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Am Kunden archivierte Scan-Belege (ZB1, alte
// Aufträge, handschriftliche Notizen) direkt an der Kundenkarte wiederfinden.
// Jun-2026: umbenannt in „Archiv" und immer sichtbar (User-Wunsch).
function CustomerDocuments({ customer }) {
    // Jul-2026 (User-Wunsch) — Deep-Link `?archiv=1` (z. B. aus „Was zu tun
    // ist") öffnet das Archiv direkt; plus Querverweis Kunde → Fahrzeug-Archiv.
    const [open, setOpen] = useState(() => {
        try { return new URLSearchParams(window.location.search).get("archiv") === "1"; } catch { return false; }
    });
    const docs = customer.archived_documents || [];
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState(null);
    useEffect(() => {
        if (!open || vehicles !== null) return;
        api.get(`/customers/${customer.id}/listing-links`)
            .then((r) => setVehicles(r.data?.items || []))
            .catch(() => setVehicles([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    return (
        <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`cust-docs-${customer.id}`}>
            <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-xs font-bold text-gray-600 hover:text-[#111827]" data-testid={`cust-docs-toggle-${customer.id}`}>
                <span className="inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Archiv ({docs.length})</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && vehicles && vehicles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`cust-vehicle-archive-${customer.id}`}>
                    {vehicles.map((v) => (
                        <button
                            key={v.id}
                            type="button"
                            onClick={() => navigate(`/edit-listing/${v.id}?focus=archiv`)}
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:border-[#FFCC00]"
                            title="Direkt zum Dokumenten-Archiv dieses Fahrzeugs"
                            data-testid={`cust-vehicle-archive-btn-${v.id}`}
                        >
                            <Bike className="w-3 h-3" /> Fahrzeug-Archiv: {v.title}
                        </button>
                    ))}
                </div>
            )}
            {open && !docs.length && (
                <p className="mt-2 text-[11px] text-gray-400" data-testid={`cust-docs-empty-${customer.id}`}>Keine archivierten Unterlagen vorhanden.</p>
            )}
            {open && (
                <div className="mt-2 space-y-1">
                    {docs.slice().reverse().map((d, i) => (
                        <a
                            key={i}
                            href={d.url?.startsWith("/") ? `${API_BASE}${d.url}` : d.url}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-[11px] hover:border-[#111827]"
                            data-testid={`cust-doc-${customer.id}-${i}`}
                        >
                            <span className="min-w-0 truncate">
                                <span className="font-bold">{DOC_KIND_LABELS[d.kind] || d.kind || "Beleg"}</span>
                                {d.name ? <span className="text-gray-500"> · {d.name}</span> : null}
                            </span>
                            <span className="text-gray-400 font-mono-num shrink-0">
                                {d.order_number ? `${d.order_number} · ` : ""}{d.archived_at ? fmtDate(d.archived_at) : ""}
                            </span>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — Ausgegebene Gutscheine am Kunden: Liste inkl.
// Status (offen/eingelöst/abgelaufen) + manuelles Anlegen neuer Gutscheine.
const VOUCHER_STATUS = {
    active: { label: "offen", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    redeemed: { label: "eingelöst", cls: "bg-gray-200 text-gray-700 border-gray-300" },
    expired: { label: "abgelaufen", cls: "bg-red-100 text-red-700 border-red-300" },
    pending_approval: { label: "wartet auf Freigabe", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    rejected: { label: "abgelehnt", cls: "bg-red-100 text-red-700 border-red-300" },
};

function CustomerVouchers({ customer }) {
    const { user } = useAuth();
    // Jul-2026 (User-Wunsch) — nur Filialleiter/Händlerkonto darf freigeben.
    const isManager = user?.role === "b2b" || user?.role === "admin" || (user?.employee_function || "").toLowerCase() === "filialleiter";
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [creating, setCreating] = useState(false);
    const [pct, setPct] = useState("10");
    const [valueType, setValueType] = useState("percent");
    const [days, setDays] = useState("30");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [templates, setTemplates] = useState(null);
    const [sendingCode, setSendingCode] = useState(null);
    const contactBlocked = customer.blocked || customer.marketing_blocked;

    const load = async () => {
        try {
            const r = await api.get(`/dealer/vouchers/by-customer/${customer.id}?include_all=true`);
            setItems(r.data?.vouchers || []);
        } catch { setItems([]); }
    };
    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && items === null) load();
    };
    const openCreate = () => {
        setCreating(true);
        if (templates === null) {
            api.get("/dealer/voucher-templates")
                .then((r) => setTemplates(r.data?.templates || []))
                .catch(() => setTemplates([]));
        }
    };
    const createVoucher = async (templateId = null) => {
        let payload;
        if (templateId) {
            payload = { customer_id: customer.id, template_id: templateId, reason: reason.trim() || undefined };
        } else {
            const p = Number(pct), d = Number(days);
            if (!p || p <= 0 || (valueType === "percent" && p > 100)) { toast.error("Bitte einen gültigen Wert angeben"); return; }
            if (!d || d < 1 || d > 365) { toast.error("Gültigkeit zwischen 1 und 365 Tagen angeben"); return; }
            payload = { customer_id: customer.id, discount_type: valueType, discount_value: p, valid_days: d, reason: reason.trim() || undefined };
        }
        setBusy(true);
        try {
            const { data } = await api.post("/dealer/vouchers", payload);
            if (data.status === "pending_approval") {
                toast.success(`Gutschein ${data.code} angelegt — wartet auf Freigabe durch den Filialleiter`);
            } else {
                toast.success(`Gutschein ${data.code} angelegt (gültig bis ${fmtDate(data.valid_until)})`);
            }
            setCreating(false);
            setReason("");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        setBusy(false);
    };
    // Jul-2026 (User-Wunsch) — Freigabe/Ablehnung durch den Filialleiter.
    const decide = async (code, action) => {
        setBusy(true);
        try {
            await api.post(`/dealer/vouchers/${code}/${action}`, {});
            toast.success(action === "approve" ? "Gutschein freigegeben — jetzt gültig & versendbar" : "Gutschein abgelehnt");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        setBusy(false);
    };
    // Jul-2026 (User-Wunsch): Gutschein direkt per E-Mail / WhatsApp an den Kunden senden.
    const sendEmail = async (code) => {
        setSendingCode(code);
        try {
            const { data } = await api.post(`/dealer/vouchers/${code}/send`);
            toast.success(data.status === "sent"
                ? `Gutschein per E-Mail an ${data.to} gesendet`
                : `E-Mail vorbereitet an ${data.to} (Versand aktiviert sich mit E-Mail-Key)`);
        } catch (e) { toast.error(e.response?.data?.detail || "Senden fehlgeschlagen"); }
        setSendingCode(null);
    };
    const voucherValLabel = (v) => v.discount_type === "eur"
        ? `${Number(v.discount_value).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`
        : `${v.discount_value || v.discount_pct} %`;
    const waVoucherLink = (v) => {
        const phone = String(customer.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
        if (!phone) return null;
        const txt = `Hallo ${customer.first_name || ""}! 🎁 Ihr Gutschein: ${v.code} — ${voucherValLabel(v)} Rabatt, gültig bis ${fmtDate(v.valid_until)}. Einfach beim nächsten Besuch nennen.`;
        return `https://wa.me/${phone}?text=${encodeURIComponent(txt)}`;
    };
    const list = items || [];
    return (
        <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`cust-vouchers-${customer.id}`}>
            <button type="button" onClick={toggle} className="w-full flex items-center justify-between text-xs font-bold text-gray-600 hover:text-[#111827]" data-testid={`cust-vouchers-toggle-${customer.id}`}>
                <span className="inline-flex items-center gap-1.5">🎁 Gutscheine{items !== null ? ` (${list.length})` : ""}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="mt-2 space-y-1.5">
                    {items === null ? (
                        <div className="text-[11px] text-gray-400">Lade…</div>
                    ) : list.length === 0 ? (
                        <div className="text-[11px] text-gray-400 italic">Keine Gutscheine vorhanden.</div>
                    ) : list.map((v) => {
                        const st = VOUCHER_STATUS[v.status] || VOUCHER_STATUS.active;
                        const wa = waVoucherLink(v);
                        return (
                            <div key={v.code} className="bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-[11px]" data-testid={`cust-voucher-${customer.id}-${v.code}`}>
                                <div className="flex items-center justify-between gap-2">
                                <span className="min-w-0 truncate">
                                    <span className="font-bold font-mono">{v.code}</span>
                                    <span className="text-gray-500"> · −{voucherValLabel(v)}</span>
                                    <span className="text-gray-400"> · bis {fmtDate(v.valid_until)}</span>
                                </span>
                                <span className="flex items-center gap-1 shrink-0">
                                    {v.status === "pending_approval" && isManager && (
                                        <>
                                            <button type="button" onClick={() => decide(v.code, "approve")} disabled={busy}
                                                className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm px-2 py-0.5 hover:bg-emerald-700 disabled:opacity-50"
                                                data-testid={`cust-voucher-approve-${v.code}`}>Freigeben</button>
                                            <button type="button" onClick={() => decide(v.code, "reject")} disabled={busy}
                                                className="text-[10px] font-bold uppercase tracking-wider bg-white text-red-600 border border-red-300 rounded-sm px-2 py-0.5 hover:bg-red-50 disabled:opacity-50"
                                                data-testid={`cust-voucher-reject-${v.code}`}>Ablehnen</button>
                                        </>
                                    )}
                                    {v.status === "active" && (
                                        <>
                                            <button type="button" onClick={() => sendEmail(v.code)}
                                                disabled={sendingCode === v.code || contactBlocked || !customer.email}
                                                className="h-6 w-6 flex items-center justify-center text-sky-700 hover:bg-sky-50 rounded-sm disabled:opacity-40"
                                                title={contactBlocked ? "Kontakt gesperrt (DSGVO/Sperre)" : (customer.email ? "Per E-Mail senden" : "Keine E-Mail hinterlegt")}
                                                data-testid={`cust-voucher-mail-${v.code}`}>
                                                <Mail className="w-3.5 h-3.5" />
                                            </button>
                                            {wa && !contactBlocked ? (
                                                <a href={wa} target="_blank" rel="noopener noreferrer"
                                                    className="h-6 w-6 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 rounded-sm"
                                                    title="Per WhatsApp senden" data-testid={`cust-voucher-wa-${v.code}`}>
                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                </a>
                                            ) : (
                                                <span className="h-6 w-6 flex items-center justify-center text-gray-300" title={contactBlocked ? "Kontakt gesperrt (DSGVO/Sperre)" : "Keine Telefonnummer"}>
                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                </span>
                                            )}
                                        </>
                                    )}
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full border ${st.cls}`} data-testid={`cust-voucher-status-${v.code}`}>
                                        {st.label}
                                    </span>
                                </span>
                                </div>
                                {v.internal_reason && (
                                    <div className="mt-0.5 text-[10px] text-gray-500 italic truncate" title={v.internal_reason} data-testid={`cust-voucher-reason-${v.code}`}>
                                        Grund (intern): {v.internal_reason}{v.created_by_name ? ` — ${v.created_by_name}` : ""}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {creating ? (
                        <div className="space-y-1.5 bg-pink-50 border border-pink-200 rounded-sm px-2 py-1.5" data-testid={`cust-voucher-create-form-${customer.id}`}>
                            {templates && templates.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-pink-700">Vorlage:</span>
                                    {templates.map((t) => (
                                        <button key={t.id} type="button" onClick={() => createVoucher(t.id)} disabled={busy}
                                            className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-pink-300 bg-white text-pink-800 hover:bg-pink-100 disabled:opacity-50"
                                            data-testid={`cust-voucher-tpl-${t.id}`}>
                                            {t.name} · −{t.value_type === "eur" ? `${Number(t.value).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : `${t.value} %`}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Input type="number" min="1" value={pct} onChange={(e) => setPct(e.target.value)} placeholder="Wert" title="Wert" className="rounded-sm h-7 w-20 text-[11px]" data-testid={`cust-voucher-pct-${customer.id}`} />
                                <span className="flex rounded-sm overflow-hidden border border-gray-300 shrink-0">
                                    <button type="button" onClick={() => setValueType("eur")} className={`px-1.5 text-[10px] font-bold ${valueType === "eur" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`cust-voucher-unit-eur-${customer.id}`}>€</button>
                                    <button type="button" onClick={() => setValueType("percent")} className={`px-1.5 text-[10px] font-bold ${valueType === "percent" ? "bg-[#111827] text-white" : "bg-white text-gray-500"}`} data-testid={`cust-voucher-unit-pct-${customer.id}`}>%</button>
                                </span>
                                <span className="text-[10px] text-gray-500">·</span>
                                <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} placeholder="Tage" title="Gültig (Tage)" className="rounded-sm h-7 w-16 text-[11px]" data-testid={`cust-voucher-days-${customer.id}`} />
                                <span className="text-[10px] text-gray-500">Tage gültig</span>
                                <button type="button" onClick={() => createVoucher()} disabled={busy}                                    className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-white rounded-sm px-2 py-1 hover:bg-black disabled:opacity-50"
                                    data-testid={`cust-voucher-create-submit-${customer.id}`}>
                                    {busy ? "…" : "Anlegen"}
                                </button>
                                <button type="button" onClick={() => setCreating(false)} className="text-[10px] text-gray-500 hover:text-gray-800">Abbrechen</button>
                            </div>
                            {/* Jul-2026 (User-Wunsch) — interner Grund (Pflicht für Mitarbeiter):
                                der Filialleiter muss wissen, warum der Kunde den Gutschein erhält. */}
                            <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
                                placeholder={isManager ? "Grund (intern, optional) — z. B. Reklamation, Kulanz…" : "Grund (intern, Pflicht) — warum erhält der Kunde den Gutschein?"}
                                title="Nur intern sichtbar — wird dem Filialleiter zur Freigabe angezeigt"
                                className="rounded-sm h-7 w-full text-[11px]" data-testid={`cust-voucher-reason-${customer.id}`} />
                            {!isManager && (
                                <div className="text-[9px] text-pink-700">Hinweis: Gutschein wird erst nach Freigabe durch den Filialleiter gültig und versendbar.</div>
                            )}
                        </div>
                    ) : (
                        <button type="button" onClick={openCreate}
                            className="w-full inline-flex items-center justify-center gap-1 text-[11px] font-bold text-pink-800 border border-dashed border-pink-300 bg-pink-50 rounded-sm py-1.5 hover:bg-pink-100"
                            data-testid={`cust-voucher-create-${customer.id}`}>
                            <Plus className="w-3 h-3" /> Gutschein anlegen
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}



function CustomerGarage({ customer, onChange }) {
    const canDelete = useCanDeleteCustomers();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [vehicles, setVehicles] = useState(null);
    const [moveVeh, setMoveVeh] = useState(null); // Fahrzeug-Objekt, das verschoben wird
    const [vehModal, setVehModal] = useState(null); // "new" | vehicle object | null

    const load = async () => {
        try {
            const r = await api.get(`/customers/${customer.id}/vehicles`);
            setVehicles(r.data || []);
        } catch { setVehicles([]); }
    };
    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && vehicles === null) load();
    };
    const remove = async (vid) => {
        if (!window.confirm("Fahrzeug aus der Garage entfernen?")) return;
        try {
            await api.delete(`/customers/${customer.id}/vehicles/${vid}`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen"); }
    };

    const custLabel = customer.company_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim();
    const count = vehicles === null ? (customer.vehicle_count ?? null) : vehicles.length;
    return (
        <div className="mt-3 pt-2 border-t border-gray-100" data-testid={`cust-garage-${customer.id}`}>
            <button type="button" onClick={toggle} className="w-full flex items-center justify-between text-xs font-bold text-gray-600 hover:text-[#111827]" data-testid={`cust-garage-toggle-${customer.id}`}>
                <span className="inline-flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Fahrzeuge{count !== null ? ` (${count})` : ""}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="mt-2 space-y-1.5">
                    {vehicles === null ? (
                        <div className="text-[11px] text-gray-400">Lade…</div>
                    ) : vehicles.length === 0 ? (
                        <div className="text-[11px] text-gray-400 italic">Keine Fahrzeuge hinterlegt.</div>
                    ) : vehicles.map((v) => (
                        <div key={v.id} className="bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-xs" data-testid={`cust-vehicle-${v.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <span className="font-bold">{v.brand} {v.model}</span>
                                    <span className="text-gray-500"> {v.license_plate || (v.vin ? `· FIN ${v.vin}` : "")}</span>
                                    {(v.first_registration || v.hu_date || v.purchased_at) && (
                                        <div className="text-[10px] text-gray-400 mt-0.5">
                                            {v.first_registration ? `EZ ${fmtDate(v.first_registration)}` : ""}
                                            {v.first_registration && v.hu_date ? " · " : ""}
                                            {v.hu_date ? `HU ${fmtDate(v.hu_date)}` : ""}
                                            {v.purchased_at && (
                                                <span className="ml-1 text-emerald-700 font-bold" data-testid={`cust-vehicle-purchased-${v.id}`}>
                                                    · Gekauft {fmtDate(v.purchased_at)}{v.purchase_seller ? ` (Verk.: ${v.purchase_seller})` : ""}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {canDelete && <button type="button" onClick={() => remove(v.id)} className="text-gray-400 hover:text-red-600 shrink-0" title="Fahrzeug entfernen (nur Filialleiter)" data-testid={`cust-vehicle-delete-${v.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                <button type="button" onClick={() => navigate(`/dealer/workshop-orders?action=new&customer=${customer.id}&vehicle=${v.id}`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#111827] hover:bg-black text-white rounded-sm px-2 py-1" data-testid={`cust-vehicle-order-${v.id}`}>
                                    <Wrench className="w-3 h-3" /> Auftrag
                                </button>
                                <button type="button" onClick={() => setVehModal(v)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Fahrzeugdaten bearbeiten" data-testid={`cust-vehicle-edit-${v.id}`}>
                                    <Pencil className="w-3 h-3" /> Bearbeiten
                                </button>
                                <button type="button" onClick={() => setMoveVeh(v)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Zu anderem Kunden verschieben" data-testid={`cust-vehicle-move-${v.id}`}>
                                    <ArrowRightLeft className="w-3 h-3" /> Verschieben
                                </button>
                            </div>
                        </div>
                    ))}
                    <button type="button" onClick={() => setVehModal("new")} className="w-full mt-1 inline-flex items-center justify-center gap-1 text-[11px] font-bold text-[#111827] border border-dashed border-gray-300 rounded-sm py-1.5 hover:border-[#111827] hover:bg-gray-50" data-testid={`cust-vehicle-add-${customer.id}`}>
                        <Plus className="w-3.5 h-3.5" /> Fahrzeug hinzufügen
                    </button>
                </div>
            )}
            {vehModal && (
                <VehicleModal
                    customerId={customer.id}
                    vehicle={vehModal === "new" ? null : vehModal}
                    onClose={() => setVehModal(null)}
                    onSaved={() => { setVehModal(null); load(); onChange?.(); }}
                />
            )}
            {moveVeh && (
                <MoveVehicleModal
                    vehicle={{ ...moveVeh, customer_id: customer.id, customer_name: custLabel }}
                    onClose={() => setMoveVeh(null)}
                    onMoved={() => { setMoveVeh(null); load(); onChange?.(); }}
                />
            )}
        </div>
    );
}

// CRM-Timeline eines Kunden (Empfehlungen, Rechnungen, Mahnungen, Werkstattaufträge).
const ACTIVITY_FILTERS = [
    { key: "all", label: "Alle" },
    { key: "invoice", label: "Rechnungen" },
    { key: "workorder", label: "Werkstatt" },
    { key: "reminder", label: "Mahnungen" },
    { key: "recommendation", label: "Empfehlungen" },
];
const ACTIVITY_TYPE_META = {
    recommendation: { Icon: MessageCircle, color: "text-amber-600" },
    invoice: { Icon: Receipt, color: "text-emerald-600" },
    reminder: { Icon: Bell, color: "text-red-600" },
    workorder: { Icon: Wrench, color: "text-blue-600" },
};
const INLINE_ACTIVITY_LIMIT = 5;

function CustomerActivities({ customer }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [filter, setFilter] = useState("all");
    const [showAll, setShowAll] = useState(false); // großes Popup mit kompletter Liste

    const load = async () => {
        try {
            const r = await api.get(`/customers/${customer.id}/activities`);
            setItems(r.data?.items || []);
        } catch { setItems([]); }
    };
    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && items === null) load();
    };

    const openEntry = async (a) => {
        if (a.type === "workorder" && a.ref_id) {
            navigate(`/dealer/workshop-orders?focus=${a.ref_id}`);
            return;
        }
        if (a.type === "invoice" && a.ref_id) {
            setBusyId(a.id);
            try {
                const res = await api.get(`/invoices/${a.ref_id}/pdf`, { responseType: "blob" });
                previewPdfResponse(res, `Rechnung_${a.ref_number || "ENTWURF"}.pdf`, { recipient: customerRecipient(customer) });
            } catch (e) { toast.error(e.response?.data?.detail || "PDF konnte nicht geladen werden"); }
            finally { setBusyId(null); }
            return;
        }
        if (a.type === "reminder" && a.ref_id && a.level) {
            setBusyId(a.id);
            try {
                const res = await api.get(`/invoices/${a.ref_id}/reminder/${a.level}/pdf`, { responseType: "blob" });
                previewPdfResponse(res, `Mahnung_St${a.level}_${a.ref_number || "Rechnung"}.pdf`, { recipient: customerRecipient(customer) });
            } catch (e) { toast.error(e.response?.data?.detail || "Mahnung konnte nicht geladen werden"); }
            finally { setBusyId(null); }
        }
    };

    const all = items || [];
    const filtered = filter === "all" ? all : all.filter((a) => a.type === filter);

    const Entry = ({ a }) => {
        const meta = ACTIVITY_TYPE_META[a.type] || { Icon: History, color: "text-gray-500" };
        const Icon = meta.Icon;
        const clickable = (a.type === "workorder" || a.type === "invoice" || (a.type === "reminder" && a.level)) && a.ref_id;
        return (
            <button type="button" onClick={() => clickable && openEntry(a)} disabled={!clickable || busyId === a.id}
                className={`w-full text-left bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-xs ${clickable ? "hover:border-[#111827] hover:bg-white cursor-pointer" : "cursor-default"}`}
                data-testid={`cust-activity-${a.id}`}>
                <div className="flex items-center gap-1.5 text-gray-800">
                    {busyId === a.id ? <Loader className="w-3 h-3 animate-spin shrink-0" /> : <Icon className={`w-3 h-3 shrink-0 ${meta.color}`} />}
                    <span className="font-bold truncate">{a.title}</span>
                    {a.badge && <span className="ml-auto shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-gray-200 text-gray-700">{a.badge}</span>}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">{fmtDate(a.date)}{a.actor ? ` · ${a.actor}` : ""}{clickable ? " · öffnen" : ""}</div>
            </button>
        );
    };

    const FilterChips = () => (
        <div className="flex flex-wrap gap-1" data-testid={`cust-activities-filters-${customer.id}`}>
            {ACTIVITY_FILTERS.map((f) => {
                const cnt = f.key === "all" ? all.length : all.filter((a) => a.type === f.key).length;
                return (
                    <button key={f.key} type="button" onClick={(e) => { e.stopPropagation(); setFilter(f.key); }}
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${filter === f.key ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
                        data-testid={`cust-activities-filter-${f.key}-${customer.id}`}>
                        {f.label} {cnt > 0 ? `(${cnt})` : ""}
                    </button>
                );
            })}
        </div>
    );

    return (
        <div className="mt-2 pt-2 border-t border-gray-100" data-testid={`cust-activities-${customer.id}`}>
            <button type="button" onClick={toggle} className="w-full flex items-center justify-between text-xs font-bold text-gray-600 hover:text-[#111827]" data-testid={`cust-activities-toggle-${customer.id}`}>
                <span className="inline-flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Verlauf{items !== null ? ` (${all.length})` : ""}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="mt-2 space-y-2">
                    {items === null ? (
                        <div className="text-[11px] text-gray-400">Lade…</div>
                    ) : all.length === 0 ? (
                        <div className="text-[11px] text-gray-400 italic">Noch keine Aktivitäten.</div>
                    ) : (
                        <>
                            <FilterChips />
                            <div className="space-y-1.5">
                                {filtered.slice(0, INLINE_ACTIVITY_LIMIT).map((a) => <Entry key={a.id} a={a} />)}
                                {filtered.length === 0 && <div className="text-[11px] text-gray-400 italic">Keine Einträge in dieser Kategorie.</div>}
                            </div>
                            {filtered.length > INLINE_ACTIVITY_LIMIT && (
                                <button type="button" onClick={() => setShowAll(true)} className="text-[11px] font-bold text-[#111827] hover:underline" data-testid={`cust-activities-showall-${customer.id}`}>
                                    Alle {filtered.length} anzeigen →
                                </button>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Großes Popup mit der kompletten (gefilterten) Timeline */}
            <Dialog open={showAll} onOpenChange={setShowAll}>
                <DialogContent className="max-w-2xl max-h-[88vh]" data-testid={`cust-activities-dialog-${customer.id}`}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-gray-500" /> Verlauf — {customer.company_name || `${customer.first_name || ""} ${customer.last_name || ""}`.trim() || "Kunde"}</DialogTitle>
                    </DialogHeader>
                    <DialogDescription className="sr-only">Vollständige Aktivitäts-Timeline des Kunden.</DialogDescription>
                    <FilterChips />
                    <div className="space-y-1.5 overflow-y-auto pr-1" style={{ maxHeight: "70vh" }} data-testid={`cust-activities-dialog-list-${customer.id}`}>
                        {filtered.length === 0 ? (
                            <div className="text-xs text-gray-400 italic py-6 text-center">Keine Einträge in dieser Kategorie.</div>
                        ) : filtered.map((a) => <Entry key={a.id} a={a} />)}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}


// Marken-/Fahrzeugtyp-Gruppen aus dem Inserate-Formular wiederverwenden (User-Wunsch).
const VEH_BRAND_GROUPS = [
    { heading: "Motorrad / Roller", items: MOTO_BRANDS.map((b) => ({ value: b })) },
    { heading: "Fahrrad / E-Bike", items: BIKE_BRANDS.map((b) => ({ value: b })) },
];
const VEH_TYPE_GROUPS = [
    { heading: "Motorrad / Roller", items: MOTORCYCLE_VEHICLE_TYPES.map((t) => ({ value: t })) },
    { heading: "Fahrrad / E-Bike", items: BIKE_TYPES.map((t) => ({ value: t })) },
];
const _BIKE_TYPE_SET = new Set(BIKE_TYPES);
// Detaillierten Fahrzeugtyp → grobe Kategorie (für kontextsensitive Werkstatt-Vorschläge).
function vehTypeToCategory(vt) {
    if (!vt) return "";
    if (_BIKE_TYPE_SET.has(vt)) return /e-bike|pedelec/i.test(vt) ? "ebike" : "fahrrad";
    if (/roller|scooter|mofa|mokick|moped/i.test(vt)) return "roller";
    return "motorrad";
}

function VehicleModal({ customerId, vehicle, onClose, onSaved }) {
    const isEdit = !!vehicle;
    const navigate = useNavigate();
    const [form, setForm] = useState({
        brand: vehicle?.brand || "",
        model: vehicle?.model || "",
        license_plate: vehicle?.license_plate || "",
        vin: vehicle?.vin || "",
        year: vehicle?.year || "",
        color: vehicle?.color || "",
        mileage: vehicle?.mileage ?? "",
        first_registration: (vehicle?.first_registration || "").slice(0, 10),
        hu_date: (vehicle?.hu_date || "").slice(0, 10),
        vehicle_type: vehicle?.vehicle_type || "",
    });
    const [busy, setBusy] = useState(false);
    // Jul-2026 (User-Wunsch) — FIN-Duplikat-Warnung: existiert die FIN schon an einem Standort?
    const [vinDup, setVinDup] = useState([]);
    useEffect(() => {
        const vin = (form.vin || "").replace(/[^A-Za-z0-9]/g, "");
        if (vin.length < 10) { setVinDup([]); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/customer-vehicles/vin-check", {
                    params: { vin, exclude_id: vehicle?.id || "" },
                });
                setVinDup(r.data.matches || []);
            } catch { setVinDup([]); }
        }, 500);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.vin]);
    const submit = async (e) => {
        e.preventDefault();
        const payload = {
            brand: form.brand || null,
            model: form.model || null,
            license_plate: form.license_plate || null,
            vin: form.vin || null,
            year: form.year ? Number(form.year) : null,
            color: form.color || null,
            mileage: form.mileage !== "" ? Number(form.mileage) : null,
            first_registration: form.first_registration || null,
            hu_date: form.hu_date || null,
            vehicle_type: form.vehicle_type || null,
            // grobe Kategorie aus dem Typ ableiten; vorhandene nicht überschreiben, wenn kein Typ gewählt
            category: form.vehicle_type ? vehTypeToCategory(form.vehicle_type) : (vehicle?.category || null),
        };
        if (!payload.brand && !payload.model && !payload.vin && !payload.license_plate) {
            toast.error("Mindestens Marke/Modell, FIN oder Kennzeichen angeben"); return;
        }
        setBusy(true);
        try {
            if (isEdit) await api.patch(`/customers/${customerId}/vehicles/${vehicle.id}`, payload);
            else await api.post(`/customers/${customerId}/vehicles`, payload);
            toast.success(isEdit ? "Fahrzeug aktualisiert" : "Fahrzeug hinzugefügt");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };
    return (
        <ModalShell onClose={onClose} title={isEdit ? "Fahrzeug bearbeiten" : "Fahrzeug hinzufügen"} testid="vehicle-modal">
            <form onSubmit={submit} className="space-y-3">
                <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Marke / Hersteller</label>
                    <BrandCombobox value={form.brand} onValueChange={(v) => setForm({ ...form, brand: v })}
                        groups={VEH_BRAND_GROUPS} placeholder="Marke wählen oder eingeben…"
                        searchPlaceholder="Hersteller suchen oder eingeben…" testId="veh-brand"
                        triggerClassName="mt-1 h-10" allowCustom />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Input placeholder="Modell" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm" data-testid="veh-model" list={`veh-models-${form.brand}`} />
                        {(BRAND_MODELS[form.brand] || []).length > 0 && (
                            <datalist id={`veh-models-${form.brand}`}>
                                {BRAND_MODELS[form.brand].map((m) => <option key={m} value={m} />)}
                            </datalist>
                        )}
                    </div>
                    <Input placeholder="Kennzeichen" value={form.license_plate} onChange={(e) => setForm({ ...form, license_plate: e.target.value })} className="rounded-sm" data-testid="veh-plate" />
                </div>
                <Input placeholder="FIN / VIN" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="rounded-sm" data-testid="veh-vin" />
                <VinWarning value={form.vin} />
                {vinDup.length > 0 && (
                    <div className="text-[11px] rounded-sm px-2.5 py-1.5 border border-amber-300 bg-amber-50 text-amber-900" data-testid="veh-vin-duplicate-warning">
                        <b>⚠ FIN bereits vorhanden:</b>
                        <ul className="mt-0.5 space-y-0.5">
                            {vinDup.slice(0, 3).map((m) => (
                                <li key={m.id}>
                                    <button
                                        type="button"
                                        onClick={() => { onClose?.(); navigate(`/dealer/invoices?tab=customers&q=${encodeURIComponent(m.customer_name || "")}`); }}
                                        className="text-left underline decoration-dotted hover:text-amber-950 truncate w-full"
                                        title="Zum bestehenden Kunden springen"
                                        data-testid={`veh-vin-dup-jump-${m.id}`}
                                    >
                                        {(m.brand || "")} {(m.model || "")} — Kunde {m.customer_name}{m.site ? ` · Standort ${m.site}` : ""} →
                                    </button>
                                </li>
                            ))}
                            {vinDup.length > 3 && <li>… und {vinDup.length - 3} weitere</li>}
                        </ul>
                    </div>
                )}
                <div>
                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Fahrzeugtyp (für passende Vorschläge)</label>
                    <BrandCombobox value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}
                        groups={VEH_TYPE_GROUPS} placeholder="Fahrzeugtyp wählen…"
                        searchPlaceholder="Fahrzeugtyp suchen…" testId="veh-vehicle-type"
                        triggerClassName="mt-1 h-10" clearLabel="– keiner –" allowCustom={false} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Farbe" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="rounded-sm" data-testid="veh-color" />
                    <Input type="number" placeholder="km-Stand" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="rounded-sm" data-testid="veh-mileage" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Erstzulassung</label>
                        <Input type="date" value={form.first_registration} onChange={(e) => setForm({ ...form, first_registration: e.target.value })} className="rounded-sm" data-testid="veh-first-registration" />
                    </div>
                    <div>
                        <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">HU/TÜV gültig bis</label>
                        <Input type="date" value={form.hu_date} onChange={(e) => setForm({ ...form, hu_date: e.target.value })} className="rounded-sm" data-testid="veh-hu-date" />
                    </div>
                </div>
                <p className="text-[11px] text-gray-400">HU-Datum manuell setzen, um die automatische Schätzung (24 Monate für Motorräder) zu überschreiben.</p>
                <Button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="veh-submit">
                    {busy ? "Speichere…" : (isEdit ? "Änderungen speichern" : "Fahrzeug hinzufügen")}
                </Button>
            </form>
        </ModalShell>
    );
}

// Jul-2026 (User-Wunsch) — Fahrzeugakte Schnellansicht: Klick auf ein Fahrzeug
// öffnet eine kompakte Akte mit Aufträgen, Rechnungen und Archiv an einem Ort.
const ORDER_STATUS_LABELS = { draft: "Entwurf", confirmed: "Bestätigt", in_progress: "In Arbeit", ready: "Fertig", done: "Abgeschlossen", delivered: "Ausgeliefert", invoiced: "Abgerechnet", cancelled: "Storniert" };
const INV_STATUS_LABELS = { draft: "Entwurf", final: "Final", paid: "Bezahlt", cancelled: "Storniert" };
const fmtEurD = (v) => (v == null ? "—" : Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

export function VehicleDossierModal({ vehicle, onClose }) {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        api.get("/dealer/vehicles/dossier", { params: { kind: vehicle.kind, id: vehicle.id } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Akte konnte nicht geladen werden"));
    }, [vehicle.kind, vehicle.id]);
    const v = data?.vehicle || vehicle;
    const cust = data?.customer;
    const MatchBadge = ({ m }) => m === "customer"
        ? <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 border border-gray-200 rounded-sm px-1 py-0.5" title="Über den Kunden zugeordnet (kein FIN/Kennzeichen-Treffer)">Kunde</span>
        : null;
    return (
        <ModalShell onClose={onClose} wide title={`Fahrzeugakte — ${(v.brand || "")} ${(v.model || v.title || "")}`.trim()} testid="vehicle-dossier-modal">
            {error ? <p className="text-sm text-red-600" data-testid="dossier-error">{error}</p> : !data ? (
                <p className="text-sm text-gray-400 py-6 text-center" data-testid="dossier-loading">Lade Akte…</p>
            ) : (
                <div className="space-y-4" data-testid="dossier-content">
                    {/* Kopf: Stammdaten */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="dossier-head">
                        <div><span className="text-gray-400 block text-[10px] uppercase">FIN</span><span className="font-mono">{v.vin || "—"}</span></div>
                        <div><span className="text-gray-400 block text-[10px] uppercase">Kennzeichen</span><span className="font-mono">{v.license_plate || "—"}</span></div>
                        <div><span className="text-gray-400 block text-[10px] uppercase">Standort</span>{v.site_code || "—"}</div>
                        <div>
                            <span className="text-gray-400 block text-[10px] uppercase">Kunde</span>
                            {cust?.name ? (
                                cust.id ? (
                                    <Link to={`/dealer/invoices?tab=customers&q=${encodeURIComponent(cust.name)}`} className="text-[#111827] font-bold hover:underline" data-testid="dossier-customer-link">
                                        {cust.name}{cust.customer_number ? ` · ${cust.customer_number}` : ""}
                                    </Link>
                                ) : <span className="font-bold">{cust.name}</span>
                            ) : "—"}
                        </div>
                    </div>

                    {/* Aug-2026 (User-Wunsch) — Kaufhistorie aus dem Verkaufs-Archiv */}
                    {(data.purchases || []).length > 0 && (
                        <div data-testid="dossier-purchases">
                            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Kaufhistorie ({data.purchases.length})</h3>
                            <ul className="divide-y divide-gray-100 border border-purple-200 bg-purple-50/40 rounded-sm">
                                {data.purchases.map((p, i) => (
                                    <li key={i} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-2.5 py-1.5 text-xs" data-testid={`dossier-purchase-${i}`}>
                                        <span className="font-bold">{p.sold_at ? new Date(p.sold_at).toLocaleDateString("de-DE") : "—"}</span>
                                        <span className="text-gray-600">Auftrag {p.order_number || "—"}</span>
                                        {p.buyer && <span className="text-gray-700">Käufer: <b>{p.buyer}</b></span>}
                                        {p.location && <span className="text-gray-500">{p.location}</span>}
                                        {p.seller && <span className="text-gray-400">Verkäufer: {p.seller}</span>}
                                        <span className="ml-auto font-mono-num font-bold">{p.price != null ? `${Number(p.price).toLocaleString("de-DE")} € nto` : "—"}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Werkstatt-Aufträge */}
                    <div data-testid="dossier-orders">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Werkstatt-Aufträge ({data.orders.length})</h3>
                        {data.orders.length === 0 ? <p className="text-xs text-gray-400">Keine Aufträge vorhanden.</p> : (
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                {data.orders.slice(0, 8).map((o) => (
                                    <li key={o.id}>
                                        <button type="button" onClick={() => navigate(`/dealer/workshop-orders?focus=${o.id}`)}
                                            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50 text-left" data-testid={`dossier-order-${o.id}`}>
                                            <span className="font-mono-num font-bold">{o.order_number || "—"}</span>
                                            <span className="text-gray-400">{fmtDate(o.appointment_at || o.created_at)}</span>
                                            <span className="text-[10px] font-bold uppercase bg-gray-100 rounded-sm px-1.5 py-0.5">{ORDER_STATUS_LABELS[o.status] || o.status}</span>
                                            <MatchBadge m={o.matched_by} />
                                            <span className="ml-auto font-mono-num">{fmtEurD(o.total_gross)}</span>
                                            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Rechnungen */}
                    <div data-testid="dossier-invoices">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5" /> Rechnungen ({data.invoices.length})</h3>
                        {data.invoices.length === 0 ? <p className="text-xs text-gray-400">Keine Rechnungen vorhanden.</p> : (
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                {data.invoices.slice(0, 8).map((inv) => (
                                    <li key={inv.id}>
                                        <button type="button" onClick={() => navigate(`/dealer/invoices?tab=invoices&q=${encodeURIComponent(inv.invoice_number || "")}`)}
                                            className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50 text-left" data-testid={`dossier-invoice-${inv.id}`}>
                                            <span className="font-mono-num font-bold">{inv.invoice_number || "Entwurf"}</span>
                                            <span className="text-gray-400">{fmtDate(inv.invoice_date)}</span>
                                            <span className="text-[10px] font-bold uppercase bg-gray-100 rounded-sm px-1.5 py-0.5">{INV_STATUS_LABELS[inv.status] || inv.status}</span>
                                            <MatchBadge m={inv.matched_by} />
                                            <span className="ml-auto font-mono-num">{fmtEurD(inv.total_gross)}</span>
                                            <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Archiv */}
                    <div data-testid="dossier-archive">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 flex items-center gap-1.5"><Archive className="w-3.5 h-3.5" /> Archiv ({data.archive.length}){v.kind === "fleet" && data.archive.length > 0 ? <span className="font-normal normal-case text-gray-400">— Unterlagen des Kunden</span> : null}</h3>
                        {data.archive.length === 0 ? <p className="text-xs text-gray-400">Keine archivierten Unterlagen.</p> : (
                            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm">
                                {data.archive.slice(0, 10).map((d, i) => (
                                    <li key={i}>
                                        <a href={d.url?.startsWith("/") ? `${API_BASE}${d.url}` : d.url} target="_blank" rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-gray-50" data-testid={`dossier-doc-${i}`}>
                                            <span className="font-bold">{DOC_KIND_LABELS[d.kind] || d.kind || "Beleg"}</span>
                                            <span className="text-gray-500 truncate">{d.name || ""}</span>
                                            <span className="ml-auto text-gray-400 font-mono-num shrink-0">{d.order_number ? `${d.order_number} · ` : ""}{d.archived_at ? fmtDate(d.archived_at) : ""}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Schnell-Aktionen */}
                    <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100" data-testid="dossier-actions">
                        {v.kind === "fleet" && cust?.id && (
                            <button type="button" onClick={() => navigate(`/dealer/workshop-orders?action=new&customer=${cust.id}&vehicle=${v.id}`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#111827] hover:bg-black text-white rounded-sm px-2.5 py-1.5" data-testid="dossier-new-order">
                                <Wrench className="w-3 h-3" /> Neuer Auftrag
                            </button>
                        )}
                        {v.kind === "inventory" && (
                            <button type="button" onClick={() => navigate(`/edit-listing/${v.id}?focus=archiv`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2.5 py-1.5" data-testid="dossier-open-listing">
                                <Pencil className="w-3 h-3" /> Inserat öffnen (Archiv)
                            </button>
                        )}
                        {v.kind === "fleet" && cust?.name && (
                            <button type="button" onClick={() => navigate(`/dealer/invoices?tab=customers&q=${encodeURIComponent(cust.name)}&archiv=1`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2.5 py-1.5" data-testid="dossier-open-customer-archive">
                                <Archive className="w-3 h-3" /> Kundenkarte &amp; Archiv
                            </button>
                        )}
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

export function VehiclesTab({ searchQ, onTotal, initialMissing = "" }) {
    const canDelete = useCanDeleteCustomers();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    // Aug-2026 (User-Wunsch Jörg): immer auf „Kundenfahrzeuge" starten
    const [kind, setKind] = useState("fleet");
    const [vehMissing, setVehMissing] = useState(initialMissing); // "" | vin | license_plate | site_code | price
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [reloadKey, setReloadKey] = useState(0);
    const [editVeh, setEditVeh] = useState(null);   // { customerId, vehicle }
    const [moveVeh, setMoveVeh] = useState(null);    // Kundenfahrzeug-Zeile
    const [dossierVeh, setDossierVeh] = useState(null); // Fahrzeugakte Schnellansicht

    useEffect(() => {
        let cancel = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/dealer/vehicles", {
                    params: { q: searchQ || "", status: kind === "fleet" ? "" : status, kind, missing: vehMissing, limit: 500 },
                });
                if (!cancel) { setData(data); onTotal?.(data.total); }
            } catch { if (!cancel) setData({ items: [], total: 0, returned: 0 }); }
            finally { if (!cancel) setLoading(false); }
        }, 250);
        return () => { cancel = true; clearTimeout(t); };
    }, [searchQ, status, kind, vehMissing, onTotal, reloadKey]);

    // Seite zurücksetzen, wenn Filter/Suche/Seitengröße wechseln
    useEffect(() => { setPage(1); }, [searchQ, status, kind, vehMissing, pageSize]);

    // Vollständige Fahrzeugdaten laden, damit beim Bearbeiten kein Feld verloren geht
    const openEdit = async (v) => {
        try {
            const { data: list } = await api.get(`/customers/${v.customer_id}/vehicles`);
            const full = (list || []).find((x) => x.id === v.id) || v;
            setEditVeh({ customerId: v.customer_id, vehicle: full });
        } catch {
            setEditVeh({ customerId: v.customer_id, vehicle: v });
        }
    };

    const KIND = [
        { k: "", l: "Alle" }, { k: "inventory", l: "Bestand/Inserate" }, { k: "fleet", l: "Kundenfahrzeuge" },
    ];
    const STATUS = [
        { k: "", l: "Alle" }, { k: "active", l: "Aktiv" }, { k: "draft", l: "Entwurf/Vorlauf" },
        { k: "sold", l: "Verkauft" }, { k: "reserved", l: "Reserviert" },
        { k: "archived", l: "Archiviert" }, { k: "deleted", l: "Gelöscht" },
    ];
    const VEH_MISSING = [
        { k: "", l: "Alle" }, { k: "vin", l: "ohne FIN" }, { k: "license_plate", l: "ohne Kennzeichen" },
        { k: "site_code", l: "ohne Standort" }, { k: "price", l: "ohne Preis (Bestand)" },
    ];
    const items = data?.items || [];
    const pageItems = items.slice((page - 1) * pageSize, page * pageSize);
    const facets = data?.facets || {};
    const STATUS_LABELS = { active: "Aktiv", draft: "Entwurf", sold: "Verkauft", reserved: "Reserviert", archived: "Archiviert", deleted: "Gelöscht" };
    // Jul-2026 (User-Wunsch) — Löschfunktion an jedem einzelnen Fahrzeug
    const deleteVehicle = async (v) => {
        const label = `${v.brand || ""} ${v.model || v.title || ""}`.trim() || "dieses Fahrzeug";
        const hint = v.kind === "fleet"
            ? "Das Kundenfahrzeug wird endgültig aus der Fahrzeugakte entfernt."
            : (["deleted", "archived"].includes(v.status)
                ? "Der Datensatz wird ENDGÜLTIG entfernt."
                : "Das Inserat wird als gelöscht markiert und aus der Übersicht entfernt.");
        if (!window.confirm(`Fahrzeug „${label}" wirklich löschen?\n\n${hint}`)) return;
        try {
            await api.delete(`/dealer/vehicles/${v.kind === "fleet" ? "fleet" : "inventory"}/${v.id}`);
            toast.success("Fahrzeug gelöscht");
            setReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
        }
    };
    const cnt = (n) => n == null ? null : (
        <span className="ml-1 opacity-60 font-normal">({Number(n).toLocaleString("de-DE")})</span>
    );

    return (
        <div data-testid="vehicles-tab">
            <div className="mb-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Typ:</span>
                {KIND.map((s) => (
                    <button key={s.k || "all"} onClick={() => setKind(s.k)}
                        className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${kind === s.k ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-300"}`}
                        data-testid={`vehicles-kind-${s.k || "all"}`}>
                        {s.l}{cnt(facets.kind?.[s.k])}
                    </button>
                ))}
                <span className="text-xs text-gray-500 ml-auto" data-testid="vehicles-count-line">
                    {data ? <><b>{Number(data.total).toLocaleString("de-DE")}</b> Fahrzeuge <span className="text-gray-400">({Number(data.inventory_total || 0).toLocaleString("de-DE")} Bestand · {Number(data.fleet_total || 0).toLocaleString("de-DE")} Kundenfz.)</span></> : null}
                </span>
            </div>
            {kind !== "fleet" && (
                <div className="mb-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Status:</span>
                    {STATUS.map((s) => (
                        <button key={s.k || "all"} onClick={() => setStatus(s.k)}
                            className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${status === s.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300"}`}
                            data-testid={`vehicles-status-${s.k || "all"}`}>
                            {s.l}{cnt(facets.status?.[s.k])}
                        </button>
                    ))}
                </div>
            )}
            {/* Jun-2026 (User-Wunsch) — Fehlende-Daten-Filter für Fahrzeuge */}
            <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="vehicles-missing-filter">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Fehlende Daten:</span>
                {VEH_MISSING.map((mm) => (
                    <button key={mm.k || "all"} onClick={() => setVehMissing(mm.k)}
                        className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${vehMissing === mm.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid={`vehicles-missing-${mm.k || "all"}`}>
                        {mm.l}{cnt(facets.missing?.[mm.k])}
                    </button>
                ))}
            </div>
            {loading ? (
                <div className="text-center py-12 text-gray-500">Lade Fahrzeuge…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500" data-testid="vehicles-empty">Keine Fahrzeuge gefunden.</div>
            ) : (
                <>
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                            <tr>
                                <th className="text-left px-3 py-2">Typ / Status</th>
                                <th className="text-left px-3 py-2">Kunde</th>
                                <th className="text-left px-3 py-2">Modell (D.3)</th>
                                <th className="text-left px-3 py-2">Hersteller</th>
                                <th className="text-left px-3 py-2">FIN</th>
                                <th className="text-left px-3 py-2">Kennzeichen</th>
                                <th className="text-left px-3 py-2">Standort</th>
                                <th className="text-right px-3 py-2">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {pageItems.map((v) => (
                                <tr key={`${v.kind}-${v.id}`} className="hover:bg-gray-50 cursor-pointer" title="Fahrzeugakte öffnen (Aufträge · Rechnungen · Archiv)" onClick={() => setDossierVeh(v)} data-testid={`vehicle-row-${v.id}`}>
                                    <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${v.kind === "fleet" ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-800"}`}>
                                            {v.kind === "fleet" ? "Kundenfz." : "Bestand"}
                                        </span>
                                        {v.kind === "inventory" && (v.sold || v.status) && (
                                            <span className="ml-1 text-[10px] text-gray-500">{v.sold ? "Verkauft" : (STATUS_LABELS[v.status] || v.status)}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {v.kind === "fleet"
                                            ? (v.customer_name
                                                ? <Link to={`/dealer/invoices?tab=customers&q=${encodeURIComponent(v.customer_name)}`}
                                                    className="text-[#111827] hover:underline font-medium inline-flex items-center gap-1"
                                                    title="Zur Kundenkarte" data-testid={`vehicle-customer-link-${v.id}`}>
                                                    <User className="w-3 h-3 text-gray-400" /> {v.customer_name}
                                                </Link>
                                                : <span className="text-gray-400">—</span>)
                                            : <Link to={`/listings/${v.id}`} className="text-[#111827] hover:underline font-medium">→ Inserat</Link>}
                                    </td>
                                    <td className="px-3 py-2">{v.model || "—"}</td>
                                    <td className="px-3 py-2">{v.brand || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-[11px]">{v.vin || "—"}</td>
                                    <td className="px-3 py-2 font-mono text-[11px]">{v.license_plate || "—"}</td>
                                    <td className="px-3 py-2">{v.site_code || "—"}</td>
                                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                            {v.kind === "fleet" ? (
                                                <>
                                                    <button type="button" onClick={() => navigate(`/dealer/workshop-orders?action=new&customer=${v.customer_id}&vehicle=${v.id}`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-[#111827] hover:bg-black text-white rounded-sm px-2 py-1" title="Neuer Werkstatt-Auftrag" data-testid={`vehicle-order-${v.id}`}>
                                                        <Wrench className="w-3 h-3" /> Auftrag
                                                    </button>
                                                    <button type="button" onClick={() => openEdit(v)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Fahrzeugdaten bearbeiten" data-testid={`vehicle-edit-${v.id}`}>
                                                        <Pencil className="w-3 h-3" /> Bearbeiten
                                                    </button>
                                                    <button type="button" onClick={() => setMoveVeh(v)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Zu anderem Kunden verschieben" data-testid={`vehicle-move-${v.id}`}>
                                                        <ArrowRightLeft className="w-3 h-3" /> Verschieben
                                                    </button>
                                                    <button type="button" onClick={() => navigate(`/dealer/invoices?tab=customers&q=${encodeURIComponent(v.customer_name || "")}&archiv=1`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Dokumenten-Archiv des Kunden/Fahrzeugs öffnen" data-testid={`vehicle-archive-${v.id}`}>
                                                        <Archive className="w-3 h-3" /> Archiv
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                <button type="button" onClick={() => navigate(`/edit-listing/${v.id}`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Inserat bearbeiten" data-testid={`vehicle-edit-${v.id}`}>
                                                    <Pencil className="w-3 h-3" /> Bearbeiten
                                                </button>
                                                <button type="button" onClick={() => navigate(`/edit-listing/${v.id}?focus=archiv`)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-gray-100 text-[#111827] border border-gray-300 rounded-sm px-2 py-1" title="Dokumenten-Archiv des Fahrzeugs öffnen" data-testid={`vehicle-archive-${v.id}`}>
                                                    <Archive className="w-3 h-3" /> Archiv
                                                </button>
                                                </>
                                            )}
                                            {canDelete && <button type="button" onClick={() => deleteVehicle(v)} className="inline-flex items-center gap-1 text-[11px] font-bold bg-white hover:bg-red-50 text-red-700 border border-red-200 rounded-sm px-2 py-1" title="Fahrzeug löschen (nur Filialleiter)" data-testid={`vehicle-delete-${v.id}`}>
                                                <Trash2 className="w-3 h-3" />
                                            </button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <PaginationBar total={items.length} page={page} pageSize={pageSize}
                    onPage={setPage} onPageSize={setPageSize} testid="vehicles-pagination" />
                </>
            )}
            {editVeh && (
                <VehicleModal
                    customerId={editVeh.customerId}
                    vehicle={editVeh.vehicle}
                    onClose={() => setEditVeh(null)}
                    onSaved={() => { setEditVeh(null); setReloadKey((k) => k + 1); }}
                />
            )}
            {moveVeh && (
                <MoveVehicleModal
                    vehicle={moveVeh}
                    onClose={() => setMoveVeh(null)}
                    onMoved={() => { setMoveVeh(null); setReloadKey((k) => k + 1); }}
                />
            )}
            {dossierVeh && (
                <VehicleDossierModal vehicle={dossierVeh} onClose={() => setDossierVeh(null)} />
            )}
        </div>
    );
}

function MoveVehicleModal({ vehicle, onClose, onMoved }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [target, setTarget] = useState(null);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);   // Neuanlage-Panel offen?
    const [nc, setNc] = useState({ type: "private", first_name: "", last_name: "", company_name: "", phone: "", email: "" });
    const [creatingBusy, setCreatingBusy] = useState(false);

    useEffect(() => {
        const term = q.trim();
        if (!term) { setResults([]); return; }
        setSearching(true);
        const h = setTimeout(async () => {
            try {
                const { data } = await api.get(`/customers?q=${encodeURIComponent(term)}&limit=20`);
                setResults((data || []).filter((c) => c.id !== vehicle.customer_id));
            } catch { setResults([]); }
            setSearching(false);
        }, 300);
        return () => clearTimeout(h);
    }, [q, vehicle.customer_id]);

    const label = (c) => c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—";

    const createCustomer = async () => {
        if (!nc.first_name.trim() || !nc.last_name.trim()) { toast.error("Vor- und Nachname Pflicht"); return; }
        setCreatingBusy(true);
        try {
            const payload = { ...nc };
            ["company_name", "phone", "email"].forEach((k) => { if (!payload[k]) payload[k] = null; });
            const { data } = await api.post("/customers", payload);
            toast.success("Neuer Kunde angelegt");
            setTarget(data);            // direkt als Ziel auswählen
            setCreating(false);
            setQ("");
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
        setCreatingBusy(false);
    };

    const move = async () => {
        if (!target) { toast.error("Ziel-Kunde wählen"); return; }
        setBusy(true);
        try {
            await api.post(`/customers/${vehicle.customer_id}/vehicles/${vehicle.id}/move`, { to_customer_id: target.id });
            toast.success("Fahrzeug verschoben — bisherige Aufträge bleiben beim Vorbesitzer");
            onMoved();
        } catch (e) { toast.error(e.response?.data?.detail || "Verschieben fehlgeschlagen"); }
        setBusy(false);
    };

    return (
        <ModalShell onClose={onClose} title="Fahrzeug zu anderem Kunden verschieben" testid="move-vehicle-modal">
            <div className="space-y-3">
                <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                    Fahrzeug: <b>{vehicle.brand} {vehicle.model}</b>{vehicle.license_plate ? ` · ${vehicle.license_plate}` : (vehicle.vin ? ` · FIN ${vehicle.vin}` : "")}
                    {vehicle.customer_name ? <> · aktuell: <b>{vehicle.customer_name}</b></> : null}
                </div>

                {target ? (
                    <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 text-sm" data-testid="move-veh-selected">
                        <span>Ziel: <b>{label(target)}</b>{target.city ? <span className="text-gray-400"> · {target.city}</span> : null}</span>
                        <button type="button" onClick={() => setTarget(null)} className="text-xs text-gray-500 hover:text-gray-800">ändern</button>
                    </div>
                ) : creating ? (
                    <div className="border border-gray-200 rounded-sm p-3 space-y-2" data-testid="move-veh-create-panel">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Neuen Kunden anlegen</span>
                            <button type="button" onClick={() => setCreating(false)} className="text-xs text-gray-500 hover:text-gray-800" data-testid="move-veh-create-cancel">Abbrechen</button>
                        </div>
                        <Select value={nc.type} onValueChange={(v) => setNc({ ...nc, type: v })}>
                            <SelectTrigger className="rounded-sm h-9" data-testid="move-veh-nc-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="private">Privatkunde</SelectItem>
                                <SelectItem value="business">Geschäftskunde</SelectItem>
                            </SelectContent>
                        </Select>
                        {nc.type === "business" && (
                            <Input placeholder="Firmenname" value={nc.company_name} onChange={(e) => setNc({ ...nc, company_name: e.target.value })} className="rounded-sm" data-testid="move-veh-nc-company" />
                        )}
                        <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Vorname *" value={nc.first_name} onChange={(e) => setNc({ ...nc, first_name: e.target.value })} className="rounded-sm" data-testid="move-veh-nc-first" />
                            <Input placeholder="Nachname *" value={nc.last_name} onChange={(e) => setNc({ ...nc, last_name: e.target.value })} className="rounded-sm" data-testid="move-veh-nc-last" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <Input placeholder="Telefon" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} className="rounded-sm" data-testid="move-veh-nc-phone" />
                            <Input type="email" placeholder="E-Mail" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} className="rounded-sm" data-testid="move-veh-nc-email" />
                        </div>
                        <Button type="button" onClick={createCustomer} disabled={creatingBusy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="move-veh-nc-submit">
                            {creatingBusy ? "Lege an…" : "Anlegen & auswählen"}
                        </Button>
                    </div>
                ) : (
                    <>
                        <Input autoFocus placeholder="Ziel-Kunde suchen (Vor-/Nachname, Firma, Kd.-Nr., Straße, Ort…)" value={q}
                            onChange={(e) => { setQ(e.target.value); setTarget(null); }} className="rounded-sm" data-testid="move-veh-search" />
                        {q.trim() ? (
                            <div className="border border-gray-200 rounded-sm max-h-52 overflow-y-auto divide-y divide-gray-100" data-testid="move-veh-results">
                                {searching ? <div className="p-3 text-xs text-gray-400">Suche…</div>
                                    : results.length === 0 ? <div className="p-3 text-xs text-gray-400 italic">Keine Treffer — lege unten einen neuen Kunden an.</div>
                                        : results.map((c) => (
                                            <button key={c.id} type="button" onClick={() => setTarget(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" data-testid={`move-veh-result-${c.id}`}>
                                                <span className="font-medium">{label(c)}</span>
                                                {c.city ? <span className="text-gray-400 text-xs"> · {c.city}</span> : null}
                                            </button>
                                        ))}
                            </div>
                        ) : null}
                        <button type="button" onClick={() => setCreating(true)} className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold text-[#111827] border border-dashed border-gray-300 rounded-sm py-2 hover:border-[#111827] hover:bg-gray-50" data-testid="move-veh-new-customer">
                            <UserPlus className="w-3.5 h-3.5" /> Neuen Kunden anlegen
                        </button>
                    </>
                )}

                <Button type="button" onClick={move} disabled={!target || busy} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm disabled:opacity-50" data-testid="move-veh-confirm">
                    {busy ? "Verschiebe…" : "Fahrzeug verschieben"}
                </Button>
            </div>
        </ModalShell>
    );
}

export function BranchBreakdown() {
    const [data, setData] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/dealer/customers/branch-counts");
                setData(data);
            } catch { /* ignore */ }
        })();
    }, []);
    if (!data || !data.branches?.length) return null;
    return (
        <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-600" data-testid="branch-breakdown">
            <span className="font-bold uppercase tracking-wider text-gray-400">Kunden je Standort:</span>
            {data.branches.map((b) => (
                <span key={b.dealer_id} data-testid={`branch-count-${b.dealer_id}`}>
                    {b.site_code ? <b>{b.site_code}</b> : null} {b.label}
                    {b.branch_role === "hauptsitz" ? " ★" : ""}: <b>{Number(b.count).toLocaleString("de-DE")}</b>
                </span>
            ))}
            <span className="text-gray-400">· Gesamt: <b>{Number(data.total).toLocaleString("de-DE")}</b></span>
        </div>
    );
}

export function CustomerSegmentBar({ onResult }) {    const [open, setOpen] = useState(false);
    const [consent, setConsent] = useState("any");
    const [brand, setBrand] = useState("");
    const [model, setModel] = useState("");
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(null);
    const [campaignOpen, setCampaignOpen] = useState(false);
    const active = consent !== "any" || brand.trim() || model.trim();

    const params = () => {
        const p = {};
        if (consent !== "any") p.consent = consent;
        if (brand.trim()) p.brand = brand.trim();
        if (model.trim()) p.model = model.trim();
        return p;
    };

    const run = async () => {
        if (!active) { onResult(null); setCount(null); return; }
        setLoading(true);
        try {
            const { data } = await api.get("/customers/segment", { params: params() });
            setCount(data.count);
            onResult(data.customers);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auswertung fehlgeschlagen");
        } finally { setLoading(false); }
    };

    const reset = () => { setConsent("any"); setBrand(""); setModel(""); setCount(null); onResult(null); };

    const exportCsv = async () => {
        try {
            const { data } = await api.get("/customers/segment.csv", { params: params(), responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url; a.download = "kundensegment.csv"; a.click();
            URL.revokeObjectURL(url);
            toast.success("CSV exportiert");
        } catch { toast.error("Export fehlgeschlagen"); }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="customer-segment-bar">
            <button type="button" onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-2 p-4 text-left" data-testid="segment-toggle">
                {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <Filter className="w-4 h-4 text-[#7C3AED]" />
                <h3 className="font-bold text-sm">Segment / Auswertung</h3>
                <span className="text-[11px] text-gray-400 hidden sm:inline">— z. B. „nur Kunden mit E-Mail-Einwilligung & Marke X" für Mailings/Service-Erinnerungen</span>
            </button>
            {open && (
            <div className="px-4 pb-4">
            <div className="flex flex-wrap items-end gap-2">
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Einwilligung</span>
                    <Select value={consent} onValueChange={setConsent}>
                        <SelectTrigger className="h-9 w-[170px] rounded-sm mt-1" data-testid="segment-consent-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="any">Alle (egal)</SelectItem>
                            <SelectItem value="email">E-Mail</SelectItem>
                            <SelectItem value="brief">Brief</SelectItem>
                            <SelectItem value="telefon">Telefon</SelectItem>
                            <SelectItem value="sms">SMS</SelectItem>
                            <SelectItem value="mobil">Mobil</SelectItem>
                            <SelectItem value="fax">Fax</SelectItem>
                        </SelectContent>
                    </Select>
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Marke (Fahrzeug)</span>
                    <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="z. B. Honda" className="h-9 w-[150px] rounded-sm mt-1" data-testid="segment-brand-input" />
                </label>
                <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Modell</span>
                    <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="z. B. CB500F" className="h-9 w-[150px] rounded-sm mt-1" data-testid="segment-model-input" />
                </label>
                <Button onClick={run} disabled={!active || loading} className="h-9 rounded-sm font-bold bg-[#7C3AED] hover:bg-[#6D28D9] text-white" data-testid="segment-run-btn">
                    {loading ? "Werte aus…" : "Auswerten"}
                </Button>
                {count !== null && (
                    <>
                        <span className="text-sm font-bold text-[#111827] px-2" data-testid="segment-count">{count} Kunden</span>
                        <Button onClick={exportCsv} variant="outline" className="h-9 rounded-sm font-bold border-[#111827]" data-testid="segment-export-btn">
                            <Download className="w-4 h-4 mr-1.5" /> CSV
                        </Button>
                        <Button onClick={() => setCampaignOpen(true)} className="h-9 rounded-sm font-bold bg-[#111827] hover:bg-black text-white" data-testid="segment-campaign-btn">
                            <Mail className="w-4 h-4 mr-1.5" /> Serien-E-Mail
                        </Button>
                        <Button onClick={reset} variant="ghost" className="h-9 rounded-sm text-xs text-gray-500" data-testid="segment-reset-btn">Zurücksetzen</Button>
                    </>
                )}
            </div>
            {campaignOpen && (
                <SegmentCampaignModal filters={params()} onClose={() => setCampaignOpen(false)} />
            )}
            </div>
            )}
        </div>
    );
}

function SegmentCampaignModal({ filters, onClose }) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [preview, setPreview] = useState(null);   // { recipients, excluded, mocked, sample }
    const [checking, setChecking] = useState(false);
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);     // campaign status

    useEffect(() => {
        let cancel = false;
        (async () => {
            setChecking(true);
            try {
                const { data } = await api.post("/customers/segment/campaign/preview", { ...filters, subject: "x", body: "y" });
                if (!cancel) setPreview(data);
            } catch { /* ignore */ } finally { if (!cancel) setChecking(false); }
        })();
        return () => { cancel = true; };
    }, []); // eslint-disable-line

    const poll = async (id) => {
        try {
            const { data } = await api.get(`/customers/segment/campaign/${id}/status`);
            setResult(data);
            if (data.status === "done") { setSending(false); toast.success(`${data.sent} E-Mails versendet`); return; }
            if (data.status === "error") { setSending(false); toast.error("Versand-Fehler"); return; }
            setTimeout(() => poll(id), 1200);
        } catch { setTimeout(() => poll(id), 2000); }
    };

    const send = async () => {
        if (!subject.trim() || !body.trim()) { toast.error("Betreff und Text erforderlich"); return; }
        setSending(true);
        try {
            const { data } = await api.post("/customers/segment/campaign", { ...filters, subject, body });
            setResult({ status: "running", total: data.recipients, sent: 0, failed: 0, mocked: data.mocked });
            poll(data.campaign_id);
        } catch (e) {
            setSending(false);
            toast.error(e?.response?.data?.detail || "Versand fehlgeschlagen");
        }
    };

    const done = result?.status === "done";
    const pct = result?.total ? Math.min(100, Math.round((result.sent / result.total) * 100)) : 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="segment-campaign-modal">
            <div className="bg-white rounded-sm w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 sticky top-0 bg-white">
                    <h2 className="font-black text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-[#7C3AED]" /> Serien-E-Mail an Segment</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="campaign-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                    {preview?.mocked && (
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800" data-testid="campaign-mock-banner">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span><strong>Test-Modus:</strong> Es ist kein Resend-Live-Key hinterlegt — der Versand wird nur protokolliert (keine echten E-Mails). Sobald der Key gesetzt ist, gehen die Mails real raus.</span>
                        </div>
                    )}
                    <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-sm" data-testid="campaign-recipient-info">
                        {checking ? <span className="text-gray-500 flex items-center gap-1.5"><Loader className="w-4 h-4 animate-spin" /> Empfänger werden ermittelt…</span> : (
                            <span><strong className="text-[#7C3AED]">{preview?.recipients ?? 0}</strong> Empfänger mit E-Mail-Einwilligung{preview?.excluded ? ` · ${preview.excluded} ohne Einwilligung/E-Mail ausgeschlossen` : ""}</span>
                        )}
                    </div>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betreff</span>
                        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z. B. HU-Erinnerung für Ihren {marke} {modell}" className="mt-1 rounded-sm" data-testid="campaign-subject" />
                    </label>
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Nachricht</span>
                        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder={"Hallo {anrede} {nachname},\nIhr {marke} {modell} steht bald zur Hauptuntersuchung an…"} className="mt-1 rounded-sm" data-testid="campaign-body" />
                    </label>
                    <div className="text-[11px] text-gray-400">
                        Platzhalter: <code>{"{anrede}"}</code> <code>{"{vorname}"}</code> <code>{"{nachname}"}</code> <code>{"{name}"}</code> <code>{"{firma}"}</code> <code>{"{marke}"}</code> <code>{"{modell}"}</code> <code>{"{kennzeichen}"}</code>
                    </div>

                    {result && (
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="campaign-progress">
                            {!done ? (
                                <>
                                    <div className="flex items-center justify-between text-xs mb-2"><span className="font-bold flex items-center gap-1.5"><Loader className="w-4 h-4 animate-spin text-[#7C3AED]" /> Versand läuft…</span><span className="text-gray-500">{result.sent} / {result.total}</span></div>
                                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#7C3AED] transition-all" style={{ width: `${pct}%` }} /></div>
                                </>
                            ) : (
                                <div className="flex items-start gap-2 text-sm" data-testid="campaign-done">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                    <div><p className="font-bold">Versand abgeschlossen</p><p className="text-xs text-gray-600">{result.sent} E-Mails {result.mocked ? "protokolliert (Test-Modus)" : "versendet"}{result.failed ? ` · ${result.failed} fehlgeschlagen` : ""}.</p></div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3 sticky bottom-0 bg-white">
                    <Button variant="outline" onClick={onClose} className="rounded-sm font-bold" data-testid="campaign-cancel">{done ? "Schließen" : "Abbrechen"}</Button>
                    {!done && (
                        <Button onClick={send} disabled={sending || checking || !preview?.recipients} className="rounded-sm font-bold bg-[#7C3AED] hover:bg-[#6D28D9] text-white disabled:opacity-50" data-testid="campaign-send-btn">
                            {sending ? <><Loader className="w-4 h-4 mr-1.5 animate-spin inline" /> Sendet…</> : <>An {preview?.recipients ?? 0} senden</>}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Fahrzeugkäufe des Kunden aus dem Verkaufs-Archiv
// (König Verkaufsexport), verknüpft über die Kunden-/Fahrzeugakte.
function CustomerPurchases({ customer }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    useEffect(() => {
        if (!open || items !== null) return;
        api.get(`/dealer/customers/${customer.id}/vehicle-purchases`)
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, [open, items, customer.id]);
    return (
        <div className="mt-2">
            <button type="button" onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] transition-colors"
                data-testid={`cust-purchases-toggle-${customer.id}`} aria-expanded={open}>
                <Receipt className="w-3.5 h-3.5" /> Fahrzeugkäufe
                <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
            {open && (
                items === null ? <p className="text-xs text-gray-400 mt-1">Lade…</p>
                : items.length === 0 ? <p className="text-xs text-gray-400 mt-1">Keine Fahrzeugkäufe im Archiv.</p>
                : (
                    <ul className="mt-1 divide-y divide-gray-100 border border-purple-200 bg-purple-50/40 rounded-sm" data-testid={`cust-purchases-${customer.id}`}>
                        {items.map((p, i) => (
                            <li key={i} className="px-2 py-1.5 text-xs flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <span className="font-bold">{p.brand} {p.model}</span>
                                <span className="text-gray-400 font-mono">{p.vin || ""}</span>
                                <span className="text-gray-600">{p.sold_at ? new Date(p.sold_at).toLocaleDateString("de-DE") : "—"}</span>
                                {p.location && <span className="text-gray-500">{p.location}</span>}
                                <span className="ml-auto font-mono-num font-bold">{p.price != null ? `${Number(p.price).toLocaleString("de-DE")} € nto` : ""}</span>
                            </li>
                        ))}
                    </ul>
                )
            )}
        </div>
    );
}

export function CustomerList({ customers, onChange, onCreateInvoice, onCreateOrder, serverPagination = null }) {
    const canDelete = useCanDeleteCustomers();
    const [editCust, setEditCust] = useState(null);
    const [waCust, setWaCust] = useState(null); // Jul-2026: Kunden-WhatsApp-Chat
    const [mergeCust, setMergeCust] = useState(null); // Aug-2026: Kunden zusammenführen
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    // Bei neuer Liste (Suche/Filter/Segment) auf Seite 1 springen
    useEffect(() => { setPage(1); }, [customers, pageSize]);
    if (!customers.length) return (
        <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="customers-empty">
            <UserPlus className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <div className="font-display font-black text-lg">Noch keine Kunden</div>
            <p className="text-sm text-gray-500 mt-1">Legen Sie Ihren ersten Kunden über den „Neuer Kunde"-Button an.</p>
        </div>
    );
    // Im Server-Modus ist `customers` bereits die aktuelle Seite — nicht clientseitig schneiden.
    const pageCustomers = serverPagination ? customers : customers.slice((page - 1) * pageSize, page * pageSize);
    return (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="customer-list">
            {pageCustomers.map((c) => (
                <div key={c.id} className="bg-white border border-gray-200 rounded-sm p-4 hover:border-[#111827] transition-colors" data-testid={`cust-${c.id}`}>
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <Badge className={`text-[10px] uppercase tracking-wider rounded-sm mb-1 ${c.type === "business" ? "bg-[#111827] text-white" : "bg-gray-200 text-gray-800"}`}>
                                {c.type === "business" ? "Firma" : "Privat"}
                            </Badge>
                            {/* Jul-2026 (User-Wunsch): Sperr-/DSGVO-Symbole in der Kundenliste */}
                            {c.blocked && (
                                <Badge className="text-[10px] uppercase tracking-wider rounded-sm mb-1 ml-1 bg-red-600 text-white" data-testid={`cust-blocked-badge-${c.id}`} title={c.blocked_reason || "Kunde gesperrt"}>
                                    ⛔ Gesperrt
                                </Badge>
                            )}
                            {c.marketing_blocked && (
                                <Badge className="text-[10px] uppercase tracking-wider rounded-sm mb-1 ml-1 bg-amber-500 text-white" data-testid={`cust-gdpr-badge-${c.id}`} title="DSGVO-Widerspruch: keinerlei Werbung/Kontakt per E-Mail, WhatsApp oder SMS">
                                    📵 DSGVO
                                </Badge>
                            )}
                            {c.customer_number && (
                                <div className="text-[10px] font-mono-num font-bold uppercase tracking-wider text-gray-500 mb-0.5" data-testid={`cust-number-${c.id}`}>
                                    Kd.-Nr. {c.customer_number}
                                </div>
                            )}
                            {c.type === "business" && c.company_name && (
                                <div className="font-display font-black text-base truncate">{c.company_name}</div>
                            )}
                            <div className="text-sm">{c.first_name} {c.last_name}</div>
                            {c.email && <div className="text-xs text-gray-500 truncate">{c.email}</div>}
                            {c.phone && (
                                <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                    <span>{c.phone}</span>
                                    {waLink(c) && (
                                        <button type="button" onClick={(e) => { e.stopPropagation(); setWaCust(c); }} className="text-emerald-600 hover:text-emerald-700" title="WhatsApp-Chat aus der Kundenakte (mit Verlauf & 24h-Regel)" data-testid={`cust-whatsapp-${c.id}`}>
                                            <MessageCircle className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                            {c.street && <div className="text-xs text-gray-500">{c.street}</div>}
                            {(c.zip || c.city) && <div className="text-xs text-gray-500">{c.zip} {c.city}</div>}
                            {c.ust_id && <div className="text-[10px] text-gray-400 mt-1">USt-IdNr.: {c.ust_id}</div>}
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                            <button type="button" onClick={() => setEditCust(c)} className="text-gray-400 hover:text-[#111827]" title="Kundendaten bearbeiten" data-testid={`cust-edit-${c.id}`}>
                                <Pencil className="w-4 h-4" />
                            </button>
                            {/* Aug-2026 (User-Wunsch) — Dubletten manuell zusammenführen */}
                            <button type="button" onClick={() => setMergeCust(c)} className="text-gray-400 hover:text-violet-700" title="Mit anderem Kunden zusammenführen (Dublette auflösen)" data-testid={`cust-merge-${c.id}`}>
                                <ArrowRightLeft className="w-4 h-4" />
                            </button>
                            {/* Jul-2026 (User-Wunsch) — Löschfunktion nur für Filialleiter */}
                            {canDelete && <button type="button"
                                onClick={async () => {
                                    const label = c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.customer_number || "diesen Kunden";
                                    if (!window.confirm(`Kunde „${label}" wirklich löschen?\n\nMit Rechnungen verknüpfte Kunden werden archiviert (Belege bleiben erhalten), alle anderen endgültig entfernt.`)) return;
                                    try {
                                        await api.delete(`/customers/${c.id}`);
                                        toast.success("Kunde gelöscht");
                                        onChange?.();
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
                                    }
                                }}
                                className="text-gray-400 hover:text-red-600" title="Kunde löschen (nur Filialleiter)" data-testid={`cust-delete-${c.id}`}>
                                <Trash2 className="w-4 h-4" />
                            </button>}
                        </div>
                    </div>
                    {(c.blocked || c.marketing_blocked) && (
                        <div className={`mt-2 text-[11px] font-bold rounded-sm px-2 py-1.5 border ${c.blocked ? "bg-red-50 border-red-300 text-red-800" : "bg-amber-50 border-amber-300 text-amber-800"}`} data-testid={`cust-warning-${c.id}`}>
                            {c.blocked
                                ? `⛔ Kunde gesperrt${c.blocked_reason ? ` — ${c.blocked_reason}` : ""}. Keine neuen Aufträge/Rechnungen.`
                                : "📵 DSGVO-Widerspruch: KEINE Werbung oder Kontaktaufnahme per E-Mail, WhatsApp oder SMS."}
                        </div>
                    )}
                    <CustomerGarage customer={c} onChange={onChange} />
                    <CustomerPurchases customer={c} />
                    <CustomerVouchers customer={c} />
                    <CustomerInvoices customer={c} />
                    <CustomerDocuments customer={c} />
                    <CustomerActivities customer={c} />
                    <div className="grid grid-cols-2 gap-2 mt-3">
                        <button type="button" onClick={() => onCreateInvoice?.(c)} className="inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] rounded-sm py-2 transition-colors" data-testid={`cust-invoice-${c.id}`}>
                            <Receipt className="w-3.5 h-3.5" /> Rechnung
                        </button>
                        <button type="button" onClick={() => onCreateOrder?.(c)} className="inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#111827] hover:bg-black text-white rounded-sm py-2 transition-colors" data-testid={`cust-order-${c.id}`}>
                            <Wrench className="w-3.5 h-3.5" /> Neuer Auftrag
                        </button>
                    </div>
                </div>
            ))}
        </div>
        <PaginationBar
            total={serverPagination ? serverPagination.total : customers.length}
            page={serverPagination ? serverPagination.page : page}
            pageSize={serverPagination ? serverPagination.pageSize : pageSize}
            onPage={serverPagination ? serverPagination.onPage : setPage}
            onPageSize={serverPagination ? serverPagination.onPageSize : setPageSize}
            testid="customers-pagination" />
        {waCust && <CustomerWhatsAppModal customer={waCust} onClose={() => setWaCust(null)} />}
        {mergeCust && (
            <MergeCustomersModal
                customer={mergeCust}
                onClose={() => setMergeCust(null)}
                onMerged={() => { setMergeCust(null); onChange?.(); }}
            />
        )}
        {editCust && (
            <EditCustomerModal
                customer={editCust}
                onClose={() => setEditCust(null)}
                onSaved={() => { setEditCust(null); onChange(); }}
            />
        )}
        </>
    );
}

const EU_VIES_COUNTRIES = new Set(["AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "GR", "EL", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE", "XI"]);

// Aug-2026 (User-Wunsch) — Zwei Kunden manuell zusammenführen: zweiten Kunden
// suchen, wählen welcher Datensatz BLEIBT, Rest wird automatisch umgehängt
// (Aufträge, Rechnungen, Fahrzeuge …) und Fahrzeug-Dubletten (FIN/Kennzeichen)
// werden dabei gleich mit vereinigt.
function MergeCustomersModal({ customer, onClose, onMerged }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [other, setOther] = useState(null);
    const [keepId, setKeepId] = useState(customer.id);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/customers?q=${encodeURIComponent(q.trim())}&limit=8`);
                setResults((Array.isArray(r.data) ? r.data : []).filter((c) => c.id !== customer.id));
            } catch { setResults([]); }
        }, 400);
        return () => clearTimeout(t);
    }, [q, customer.id]);
    const label = (c) => (c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email || c.id.slice(0, 8));
    const sub = (c) => [c.customer_number ? `Kd.-Nr. ${c.customer_number}` : null, c.email, c.phone, [c.zip, c.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "—";
    const doMerge = async () => {
        const keep = keepId === customer.id ? customer : other;
        const dissolve = keepId === customer.id ? other : customer;
        if (!window.confirm(`„${label(dissolve)}" wird in „${label(keep)}" aufgelöst.\n\nAlle Aufträge, Rechnungen und Fahrzeuge werden übernommen. Das kann nicht rückgängig gemacht werden — fortfahren?`)) return;
        setBusy(true);
        try {
            const r = await api.post("/customers/merge", { source_id: dissolve.id, target_id: keep.id });
            toast.success(`Zusammengeführt — ${r.data.repointed} Verknüpfung(en) übernommen${r.data.vehicles_merged ? `, ${r.data.vehicles_merged} Fahrzeug-Dublette(n) vereinigt` : ""}`);
            onMerged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zusammenführen fehlgeschlagen");
        } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="merge-customers-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-4 h-4 text-violet-700" /> Kunden zusammenführen</DialogTitle>
                    <DialogDescription>Dublette auflösen: zweiten Kunden suchen und wählen, welcher Datensatz bestehen bleibt.</DialogDescription>
                </DialogHeader>
                <div className="border border-gray-200 rounded-sm p-2.5 bg-gray-50">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kunde 1</div>
                    <div className="text-sm font-bold">{label(customer)}</div>
                    <div className="text-[11px] text-gray-500">{sub(customer)}</div>
                </div>
                {!other ? (
                    <div>
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zweiten Kunden suchen (Name, E-Mail, Kd.-Nr. …)" className="h-10" autoFocus data-testid="merge-search-input" />
                        {results.length > 0 && (
                            <div className="mt-1 border border-gray-200 rounded-sm divide-y max-h-56 overflow-auto" data-testid="merge-search-results">
                                {results.map((c) => (
                                    <button key={c.id} type="button" onClick={() => { setOther(c); setKeepId(customer.id); }}
                                        className="w-full text-left px-3 py-2 hover:bg-violet-50" data-testid={`merge-hit-${c.id}`}>
                                        <div className="text-sm font-bold">{label(c)}</div>
                                        <div className="text-[11px] text-gray-500">{sub(c)}</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="border border-violet-300 rounded-sm p-2.5 bg-violet-50">
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Kunde 2</div>
                                <button type="button" onClick={() => setOther(null)} className="text-[11px] text-gray-500 hover:text-black underline" data-testid="merge-change-other">ändern</button>
                            </div>
                            <div className="text-sm font-bold">{label(other)}</div>
                            <div className="text-[11px] text-gray-500">{sub(other)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Welcher Datensatz bleibt bestehen?</div>
                            {[customer, other].map((c) => (
                                <label key={c.id} className={`flex items-center gap-2 border rounded-sm px-3 py-2 mb-1.5 cursor-pointer ${keepId === c.id ? "border-violet-500 bg-violet-50" : "border-gray-200"}`}>
                                    <input type="radio" name="merge-keep" checked={keepId === c.id} onChange={() => setKeepId(c.id)} data-testid={`merge-keep-${c.id}`} />
                                    <span className="text-sm font-bold">{label(c)}</span>
                                    <span className="text-[11px] text-gray-500 truncate">{sub(c)}</span>
                                </label>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={onClose} className="h-9 rounded-sm font-bold" data-testid="merge-cancel-btn">Abbrechen</Button>
                            <Button onClick={doMerge} disabled={busy} className="h-9 rounded-sm bg-violet-700 hover:bg-violet-800 text-white font-bold" data-testid="merge-confirm-btn">
                                {busy ? <Loader className="w-4 h-4 animate-spin" /> : <><ArrowRightLeft className="w-4 h-4 mr-1.5" /> Zusammenführen</>}
                            </Button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Jul-2026 (User-Wunsch): USt-IdNr.-Feld mit qualifizierter EU-VIES-Prüfung
// (Bestätigungsnachweis als PDF für steuerfreie innergem. Lieferungen).
export function UstIdField({ value, onChange, customerId, initialCheck, testid }) {
    const [check, setCheck] = useState(initialCheck || null);
    const [busy, setBusy] = useState(false);
    const runCheck = async () => {
        if (!value || value.trim().length < 4) { toast.error("Bitte zuerst eine USt-IdNr. eingeben (z. B. ATU12345678)"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/tax/vies-check", { vat: value.trim(), customer_id: customerId || null });
            setCheck(data);
            if (data.valid === true) toast.success(`USt-IdNr. gültig (VIES)${data.consultation_number ? " — Bestätigungsnachweis gespeichert" : ""}`);
            else if (data.valid === false) toast.error("USt-IdNr. laut VIES UNGÜLTIG — keine steuerfreie Lieferung möglich!");
            else toast.warning(data.error || "VIES aktuell nicht erreichbar — bitte später erneut prüfen.");
        } catch (e) { toast.error(e.response?.data?.detail || "VIES-Prüfung fehlgeschlagen"); }
        setBusy(false);
    };
    const downloadProof = async () => {
        try {
            const r = await api.get(`/tax/vies-checks/${check.id}/proof.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `VIES-Nachweis_${(check.vat || `${check.country_code || ""}${check.vat_number || ""}`)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("Nachweis-PDF konnte nicht geladen werden"); }
    };
    return (
        <div className="space-y-1">
            <div className="flex gap-2">
                <Input placeholder="USt-IdNr. (z. B. ATU12345678)" value={value} onChange={(e) => { onChange(e.target.value); setCheck(null); }} className="rounded-sm flex-1" data-testid={testid} />
                <Button type="button" variant="outline" onClick={runCheck} disabled={busy} className="h-10 rounded-sm text-xs font-bold shrink-0" data-testid={`${testid}-vies-btn`}>
                    {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : "VIES prüfen"}
                </Button>
            </div>
            {check && (
                check.valid === true ? (
                    <div className="text-[11px] bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5 text-emerald-800" data-testid={`${testid}-vies-valid`}>
                        <b>✓ Gültig (VIES)</b>{check.name ? ` — ${check.name}` : ""}
                        {check.consultation_number ? <span className="block text-emerald-700">Konsultationsnr.: {check.consultation_number}</span> : null}
                        {check.id && (
                            <button type="button" onClick={downloadProof} className="underline font-bold mt-0.5 block" data-testid={`${testid}-vies-proof`}>Bestätigungsnachweis (PDF) speichern</button>
                        )}
                    </div>
                ) : check.valid === false ? (
                    <div className="text-[11px] bg-red-50 border border-red-200 rounded-sm px-2 py-1.5 text-red-800 font-bold" data-testid={`${testid}-vies-invalid`}>
                        ✗ UNGÜLTIG laut VIES — steuerfreie innergemeinschaftliche Lieferung NICHT zulässig.
                    </div>
                ) : (
                    <div className="text-[11px] bg-gray-50 border border-gray-200 rounded-sm px-2 py-1.5 text-gray-600" data-testid={`${testid}-vies-unknown`}>
                        {check.error || "VIES nicht erreichbar — bitte später erneut prüfen."}
                        {check.checked_at ? ` (${fmtDate(check.checked_at)})` : ""}
                    </div>
                )
            )}
        </div>
    );
}

function EditCustomerModal({ customer, onClose, onSaved }) {
    const [form, setForm] = useState({
        type: customer.type || "private",
        first_name: customer.first_name || "",
        last_name: customer.last_name || "",
        company_name: customer.company_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        street: customer.street || "",
        zip: customer.zip || "",
        city: customer.city || "",
        country: customer.country || "DE",
        ust_id: customer.ust_id || "",
        tax_number: customer.tax_number || "",
        buyer_reference: customer.buyer_reference || "",
        notes: customer.notes || "",
        marketing_blocked: !!customer.marketing_blocked,
        blocked: !!customer.blocked,
        blocked_reason: customer.blocked_reason || "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.first_name || !form.last_name) { toast.error("Vor- und Nachname Pflicht"); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            // Leere optionale Felder als null senden (EmailStr-Validierung)
            ["email", "phone", "company_name", "street", "zip", "city", "ust_id", "tax_number", "buyer_reference", "notes", "blocked_reason"].forEach((k) => {
                if (!payload[k]) payload[k] = null;
            });
            await api.patch(`/customers/${customer.id}`, payload);
            toast.success("Kundendaten aktualisiert");
            onSaved();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };
    return (
        <ModalShell onClose={onClose} title="Kunde bearbeiten" testid="edit-customer-modal">
            <form onSubmit={submit} className="space-y-3">
                {/* Jul-2026 (User-Wunsch): Kundennummer immer sichtbar */}
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Kundennummer</span>
                    <span className="font-mono-num font-black text-sm" data-testid="edit-cust-number">{customer.customer_number || "—"}</span>
                </div>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="edit-cust-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="private">Privatkunde</SelectItem>
                        <SelectItem value="business">Geschäftskunde</SelectItem>
                    </SelectContent>
                </Select>
                {form.type === "business" && (
                    <Input placeholder="Firmenname" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-sm" data-testid="edit-cust-company" />
                )}
                <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Vorname *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-sm" data-testid="edit-cust-first" />
                    <Input placeholder="Nachname *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-sm" data-testid="edit-cust-last" />
                </div>
                <Input type="email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="edit-cust-email" />
                <Input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" data-testid="edit-cust-phone" />
                <Input placeholder="Straße & Hausnummer" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} className="rounded-sm" data-testid="edit-cust-street" />
                <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="PLZ" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="rounded-sm" data-testid="edit-cust-zip" />
                    <Input placeholder="Stadt" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm col-span-2" data-testid="edit-cust-city" />
                </div>
                {form.type === "business" && (
                    <UstIdField value={form.ust_id} onChange={(v) => setForm({ ...form, ust_id: v })} customerId={customer.id} initialCheck={customer.vies_check} testid="edit-cust-ust" />
                )}
                {form.type === "business" && (
                    <Input placeholder="Leitweg-ID / Käuferreferenz (XRechnung, optional)" value={form.buyer_reference} onChange={(e) => setForm({ ...form, buyer_reference: e.target.value })} className="rounded-sm" data-testid="edit-cust-buyer-ref" />
                )}
                <Textarea placeholder="Notizen zum Kunden" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm" data-testid="edit-cust-notes" />
                {/* Jul-2026 (User-Wunsch): Datenschutz (DSGVO-Widerruf) & Kundensperre */}
                <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 space-y-1" data-testid="edit-cust-gdpr-block">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.marketing_blocked} onChange={(e) => setForm({ ...form, marketing_blocked: e.target.checked })} className="mt-0.5" data-testid="edit-cust-marketing-blocked" />
                        <span className="text-xs">
                            <span className="font-bold">📵 DSGVO-Widerruf / Werbesperre</span>
                            <span className="block text-gray-600">Kunde erhält KEINERLEI Werbung oder sonstige Kontaktaufnahme per E-Mail, WhatsApp oder SMS.</span>
                        </span>
                    </label>
                </div>
                <div className="border border-red-300 bg-red-50 rounded-sm p-3 space-y-2" data-testid="edit-cust-block-block">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.blocked} onChange={(e) => setForm({ ...form, blocked: e.target.checked })} className="mt-0.5" data-testid="edit-cust-blocked" />
                        <span className="text-xs">
                            <span className="font-bold text-red-800">⛔ Kunde sperren</span>
                            <span className="block text-gray-600">Kunde ist nicht mehr erwünscht — keine neuen Aufträge/Rechnungen, kein Versand.</span>
                        </span>
                    </label>
                    {form.blocked && (
                        <Input placeholder="Sperrgrund (optional)" value={form.blocked_reason} onChange={(e) => setForm({ ...form, blocked_reason: e.target.value })} className="rounded-sm bg-white" data-testid="edit-cust-blocked-reason" />
                    )}
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="edit-cust-submit">
                    {busy ? "Speichere…" : "Änderungen speichern"}
                </Button>
            </form>
        </ModalShell>
    );
}

export function CreateCustomerModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        type: "private", first_name: "", last_name: "", company_name: "",
        email: "", phone: "", street: "", zip: "", city: "", country: "DE",
        ust_id: "", tax_number: "", buyer_reference: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        if (!form.first_name || !form.last_name) { toast.error("Vor- und Nachname Pflicht"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/customers", form);
            toast.success(`Kunde angelegt — Kundennummer: ${data.customer_number}`, { duration: 8000 });
            onCreated();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };
    return (
        <ModalShell onClose={onClose} title="Neuer Kunde" testid="new-customer-modal">
            <form onSubmit={submit} className="space-y-3">
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="cust-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="private">Privatkunde</SelectItem>
                        <SelectItem value="business">Geschäftskunde</SelectItem>
                    </SelectContent>
                </Select>
                {form.type === "business" && (
                    <Input placeholder="Firmenname *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="rounded-sm" data-testid="cust-company" />
                )}
                <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Vorname *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-sm" data-testid="cust-first" />
                    <Input placeholder="Nachname *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-sm" data-testid="cust-last" />
                </div>
                <Input type="email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm" data-testid="cust-email" />
                <Input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" data-testid="cust-phone" />
                <Input placeholder="Straße & Hausnummer" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} className="rounded-sm" data-testid="cust-street" />
                <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="PLZ" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="rounded-sm" data-testid="cust-zip" />
                    <Input placeholder="Stadt" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm col-span-2" data-testid="cust-city" />
                </div>
                {form.type === "business" && (
                    <UstIdField value={form.ust_id} onChange={(v) => setForm({ ...form, ust_id: v })} testid="cust-ust" />
                )}
                {form.type === "business" && (
                    <Input placeholder="Leitweg-ID / Käuferreferenz (XRechnung, optional)" value={form.buyer_reference} onChange={(e) => setForm({ ...form, buyer_reference: e.target.value })} className="rounded-sm" data-testid="cust-buyer-ref" />
                )}
                <Button type="submit" disabled={busy} className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm" data-testid="cust-submit">
                    {busy ? "Speichere…" : "Kunde anlegen"}
                </Button>
            </form>
        </ModalShell>
    );
}
